import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Package, Truck, Download, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { SHOP } from "@/constants/testIds";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = useDict(lang);
  const { add } = useCart();

  const [product, setProduct] = useState(null);
  const [err, setErr] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let mounted = true;
    setProduct(null);
    setErr(false);
    api.getProduct(slug)
      .then((p) => { if (mounted) setProduct(p); })
      .catch(() => { if (mounted) setErr(true); });
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    api.listProducts({ category: product.category })
      .then((items) => setSimilar(items.filter((it) => it.slug !== product.slug).slice(0, 4)))
      .catch(() => {});
  }, [product]);

  // SEO: dynamic <title>, meta, JSON-LD Product
  useEffect(() => {
    if (!product) return undefined;
    const name = product.name[lang] || product.name.fr;
    const desc = product.description[lang] || product.description.fr || "";

    const prevTitle = document.title;
    document.title = `${name} — BAYO Boutique`;

    const setMeta = (sel, content) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        const isOg = sel.includes("property=");
        const attr = isOg ? "property" : "name";
        const m = sel.match(/\[(?:name|property)="([^"]+)"\]/);
        if (m) el.setAttribute(attr, m[1]);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta('meta[name="description"]', desc.slice(0, 200));
    setMeta('meta[property="og:title"]', `${name} — BAYO`);
    setMeta('meta[property="og:description"]', desc.slice(0, 200));
    setMeta('meta[property="og:image"]', product.image);
    setMeta('meta[property="og:type"]', "product");

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": name,
      "description": desc,
      "image": [product.image],
      "category": product.category,
      "brand": { "@type": "Brand", "name": "BAYO" },
      "offers": {
        "@type": "Offer",
        "price": product.price.toFixed(2),
        "priceCurrency": "EUR",
        "availability": product.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        "url": window.location.href,
      },
    };
    let ld = document.head.querySelector('script[type="application/ld+json"][data-bayo-product]');
    if (!ld) {
      ld = document.createElement("script");
      ld.setAttribute("type", "application/ld+json");
      ld.setAttribute("data-bayo-product", "1");
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(jsonLd);

    return () => {
      document.title = prevTitle;
      const el = document.head.querySelector('script[type="application/ld+json"][data-bayo-product]');
      if (el) el.remove();
    };
  }, [product, lang]);

  const handleAdd = () => {
    if (!product || product.stock <= 0) return;
    add(product, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const stockLine = useMemo(() => {
    if (!product) return "";
    if (product.stock <= 0) return t.shop.outOfStock;
    if (product.stock <= 5) return t.shop.lowStock.replace("{n}", product.stock);
    return t.shop.inStock;
  }, [product, t]);

  if (err) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 text-center">
        <p data-testid={SHOP.detailNotFound} className="font-serif-display text-3xl italic mb-6">
          {lang === "fr" ? "Produit introuvable" : "Product not found"}
        </p>
        <Link to="/shop" className="text-xs tracking-[0.22em] uppercase underline">
          {t.shop.backToShop}
        </Link>
      </div>
    );
  }
  if (!product) return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32" />;

  const name = product.name[lang] || product.name.fr;
  const desc = product.description[lang] || product.description.fr || "";

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 pb-24">
      <Link to="/shop" data-testid={SHOP.detailBack} className="text-xs tracking-[0.22em] uppercase font-medium hover:opacity-60">
        {t.shop.backToShop}
      </Link>

      <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-7">
          <div className="aspect-square overflow-hidden bg-[var(--bg-2)]">
            <img src={product.image} alt={name} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="lg:col-span-5 lg:pt-6">
          <p className="eyebrow mb-4 flex items-center gap-2">
            <Tag size={12} className="inline" /> {product.category}
          </p>
          <h1 data-testid={SHOP.detailTitle} className="font-serif-display text-5xl lg:text-6xl leading-[1.02] mb-6">
            {name}
          </h1>

          <p className="text-base text-[color:var(--ink-2)] leading-relaxed mb-8">{desc}</p>

          <div className="flex items-baseline gap-4 mb-8">
            <span className="font-serif-display text-5xl">{product.price.toFixed(2)} €</span>
            <span className={`text-xs tracking-[0.22em] uppercase ${product.stock <= 0 ? "text-[color:var(--red)]" : "text-[color:var(--palm)]"}`}>
              · {stockLine}
            </span>
          </div>

          <button
            data-testid={SHOP.detailAddToCart}
            onClick={handleAdd}
            disabled={product.stock <= 0}
            className="btn-navy w-full justify-center mb-4 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {product.stock <= 0
              ? t.shop.outOfStock
              : added
                ? `${t.shop.added} ✓`
                : t.shop.addToCart}
          </button>

          <Link to="/shop" className="block text-center text-xs tracking-[0.22em] uppercase underline text-[color:var(--ink-2)] mb-10">
            {t.shop.continueShopping}
          </Link>

          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="flex items-start gap-3 py-3 border-t border-[color:rgba(0,16,48,0.12)]">
              {product.is_digital ? <Download size={16} className="mt-0.5 text-[color:var(--palm)]" /> : <Truck size={16} className="mt-0.5 text-[color:var(--ink-2)]" />}
              <p className="text-[color:var(--ink-2)] leading-relaxed">
                {product.is_digital ? t.shop.digitalNote : t.shop.physicalNote}
              </p>
            </div>
            <div className="flex items-start gap-3 py-3 border-t border-[color:rgba(0,16,48,0.12)]">
              <Package size={16} className="mt-0.5 text-[color:var(--ink-2)]" />
              <p className="text-[color:var(--ink-2)] leading-relaxed">
                {t.shop.category} : <span className="font-medium text-[color:var(--ink)] capitalize">{product.category}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {similar.length > 0 && (
        <section className="mt-24 pt-16 border-t border-[color:rgba(0,16,48,0.12)]">
          <p className="eyebrow mb-6">{t.shop.similar}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {similar.map((p) => (
              <Link
                key={p.product_id}
                to={`/shop/${p.slug}`}
                data-testid={SHOP.similarCard(p.slug)}
                onClick={() => window.scrollTo({ top: 0 })}
                className="product-card group block"
              >
                <div className="product-img"><img src={p.image} alt="" /></div>
                <div className="pt-4">
                  <h3 className="font-serif-display text-lg leading-tight">{p.name[lang] || p.name.fr}</h3>
                  <p className="mt-2 font-serif-display text-xl">{p.price.toFixed(2)} €</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
